import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  addToSharedCart,
  decreaseSharedQuantity,
  removeFromSharedCart,
} from "../../redux/slices/sharedCartSlice";
import { IFacility } from "../../types";
import {
  ArabicCode,
  BASE_LOCAL_IMAGES_DIR,
  CardCounterSpeed,
  MAX_CART_QUANTITY,
} from "../../utils/constants/CommonConstants";
import {
  cart_trash_IMG,
  minus_button_IMG,
  white_plus_IMG,
} from "../../utils/constants/Images";

interface CartTableItemQuantityItemProps {
  TableRow: {
    row: any;
    Facility: IFacility;
  };
}
const SharedCartTableItemQuantityItem: React.FC<
  CartTableItemQuantityItemProps
> = ({ TableRow }) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const uniqueKey = TableRow?.row?.productId
    ? `product-${TableRow?.row?.productId}`
    : TableRow?.row?.offerId
      ? `offer-${TableRow?.row?.offerId}`
      : `package-${TableRow?.row?.packageId}`;

  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>(
    {},
  );
  const [isIncreasing, setIsIncreasing] = useState<Record<string, boolean>>({});
  const [isDecreasing, setIsDecreasing] = useState<Record<string, boolean>>({});
  const handleAddItemToCart = () => {
    const currentQty = itemQuantities[uniqueKey] || 0;
    if (currentQty >= MAX_CART_QUANTITY) return;

    dispatch(
      addToSharedCart({
        productId: TableRow.row?.productId || 0,
        offerId: TableRow.row?.offerId || 0,
        packageId: TableRow.row?.packageId || 0,
        quantity: 1,
        price: TableRow.row?.price,
        image: TableRow.row?.image,
        name: TableRow.row?.name,
        nameAr: TableRow.row?.nameAr,
        nameEn: TableRow.row?.nameEn,
        warehouse: TableRow.row?.warehouse,
        itemType: TableRow.row?.itemType,
        currency: TableRow.row?.currency,
      }),
    );
  };

  const handleRemoveItem = () => {
    const currentQty = itemQuantities[uniqueKey] || 0;
    if (currentQty === 1) {
      dispatch(
        removeFromSharedCart({
          productId: TableRow?.row?.productId,
          offerId: TableRow?.row?.offerId,
          packageId: TableRow?.row?.packageId,
          warehouse: TableRow?.row?.warehouse,
        }),
      );
    } else if (currentQty > 1) {
      dispatch(
        decreaseSharedQuantity({
          productId: TableRow?.row?.productId,
          offerId: TableRow?.row?.offerId,
          packageId: TableRow?.row?.packageId,
          quantity: 1,
          warehouse: TableRow?.row?.warehouse,
        }),
      );
    }
  };

  const handleQuantityChange = (value: string) => {
    const currentQty = itemQuantities[uniqueKey] || 1;
    let parsed = Math.floor(Number(value));
    if (isNaN(parsed) || parsed < 1) parsed = 1;
    if (parsed > MAX_CART_QUANTITY) parsed = MAX_CART_QUANTITY;

    setItemQuantities((prev) => ({ ...prev, [uniqueKey]: parsed }));
    const diff = parsed - currentQty;

    if (diff > 0) {
      dispatch(
        addToSharedCart({
          productId: TableRow.row?.productId || 0,
          offerId: TableRow.row?.offerId || 0,
          packageId: TableRow.row?.packageId || 0,
          quantity: diff,
          price: TableRow.row?.price,
          image: TableRow.row?.image,
          name: TableRow.row?.name,
          nameAr: TableRow.row?.nameAr,
          nameEn: TableRow.row?.nameEn,
          warehouse: TableRow.row?.warehouse,
          itemType: TableRow.row?.itemType,
          currency: TableRow.row?.currency,
        }),
      );
    } else if (diff < 0) {
      dispatch(
        decreaseSharedQuantity({
          productId: TableRow?.row?.productId,
          offerId: TableRow?.row?.offerId,
          packageId: TableRow?.row?.packageId,
          quantity: Math.abs(diff),
          warehouse: TableRow?.Facility,
        }),
      );
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isIncreasing[uniqueKey]) {
      const increment = () => {
        const currentQty = itemQuantities[uniqueKey] || 0;
        if (currentQty >= MAX_CART_QUANTITY) return;
        setItemQuantities((prev) => ({ ...prev, [uniqueKey]: currentQty + 1 }));
        handleAddItemToCart();
        timeout = setTimeout(increment, CardCounterSpeed);
      };
      timeout = setTimeout(increment, CardCounterSpeed);
    }
    return () => clearTimeout(timeout);
  }, [isIncreasing[uniqueKey], itemQuantities[uniqueKey]]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isDecreasing[uniqueKey]) {
      const decrement = () => {
        setItemQuantities((prev) => ({
          ...prev,
          [uniqueKey]: Math.max((prev[uniqueKey] || 0) - 1, 0),
        }));
        handleRemoveItem();
        timeout = setTimeout(decrement, CardCounterSpeed);
      };
      decrement();
    }
    return () => clearTimeout(timeout);
  }, [isDecreasing[uniqueKey]]);

  useEffect(() => {
    setItemQuantities((prev) => ({
      ...prev,
      [uniqueKey]: TableRow?.row?.quantity,
    }));
  }, [TableRow?.row?.quantity, uniqueKey]);

  return (
    <div className="flex items-center gap-0">
      <button
        onMouseDown={() =>
          setIsDecreasing((prev) => ({ ...prev, [uniqueKey]: true }))
        }
        onMouseUp={() =>
          setIsDecreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        onMouseLeave={() =>
          setIsDecreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        className={`flex h-8 w-8 items-center justify-center bg-componentFilterOutline ${
          i18n.language === ArabicCode ? "rounded-r-md" : "rounded-l-md"
        }`}
        // disabled={itemQuantities[uniqueKey] <= 0}
        disabled={true}
      >
        <img
          src={`${BASE_LOCAL_IMAGES_DIR}/${
            itemQuantities[uniqueKey] === 1 ? cart_trash_IMG : minus_button_IMG
          }`}
          alt="minus"
        />
      </button>

      <input
        type="number"
        min={1}
        disabled={true}
        max={MAX_CART_QUANTITY}
        className="h-8 w-full bg-primaryLighter text-center outline-none"
        value={
          typeof itemQuantities[uniqueKey] === "number"
            ? Math.min(
                Math.max(1, Math.floor(itemQuantities[uniqueKey])),
                MAX_CART_QUANTITY,
              )
            : 1
        }
        // onKeyDown={(e) => {
        //   if ([".", ",", "e"].includes(e.key)) e.preventDefault();
        // }}
        // onChange={(e) => handleQuantityChange(e.target.value)}
      />

      <button
        onMouseDown={() =>
          setIsIncreasing((prev) => ({ ...prev, [uniqueKey]: true }))
        }
        onMouseUp={() =>
          setIsIncreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        onMouseLeave={() =>
          setIsIncreasing((prev) => ({ ...prev, [uniqueKey]: false }))
        }
        // onClick={handleAddItemToCart}
        disabled={true}
        className={`flex h-8 w-8 items-center justify-center bg-primaryMain ${
          i18n.language === ArabicCode ? "rounded-l-md" : "rounded-r-md"
        }`}
      >
        <img src={`${BASE_LOCAL_IMAGES_DIR}/${white_plus_IMG}`} alt="plus" />
      </button>
    </div>
  );
};

export default SharedCartTableItemQuantityItem;
